import {
  AddCircle,
  BorderColor,
  Description,
  Search,
  Visibility,
} from "@mui/icons-material";
import { Box, Chip, Grid, TextField, Typography } from "@mui/material";
import useApi from "_api/index";
import Actions from "components/Car/Actions";
import TableData from "components/Car/Table";
import ChipCustomize from "components/Chip";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import ModalConsignmentCar from "components/Modal/Car/ModalConsignmentCar";
import ModalDetailCar from "components/Modal/Car/ModalDetailCar";
import CustomPagination from "components/TableExplanded/Pagination";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatCurrency2Digit, formatDateTime } from "utils/helpers/format";

const ConsignmentCars = () => {
  const [loading, setLoading] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [consignmentCars, setConsignmentCars] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [filter, setFilter] = useState({
    search: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [pagination.page, pagination.limit]);

  const fetchData = async (is_clear = false) => {
    setLoading(true);
    try {
      const response = await useApi.consignment_car.getConsignmentCarsList({
        page: pagination.page,
        limit: pagination.limit,
        ...(is_clear ? {} : filter),
      });

      if (response.success) {
        const { data, meta } = response;
        setConsignmentCars(data ?? []);
        setPagination({
          page: meta.page || 1,
          limit: meta.limit || 10,
          total: meta.total || 0,
          totalPages: meta.totalPages || 1,
        });
      }
    } catch (error) {
      console.error("Error fetching consignment cars:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsDetailModalOpen(false);
    setSelectedCar(null);
    setIsEditMode(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography component={"div"} sx={{ fontSize: "32px" }}>
          รายการฝากขาย
        </Typography>
        <GradientButton
          onClick={() => {
            // Handle create new car action
            setIsModalOpen(true);
          }}
          sx={{
            px: 3,
            height: "48px",
          }}
          endIcon={<AddCircle fontSize="medium" />}
        >
          รายการฝากขาย
        </GradientButton>
      </Box>

      <Grid
        container
        spacing={2}
        alignContent="flex-end"
        justifyContent="flex-end"
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} sm={6} md={4}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs={4} textAlign="right">
              <Typography
                sx={{
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                ค้นหาทั้งหมด
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="ค้นหาทั้งหมด"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    height: "40px",
                  },
                }}
                value={filter.search}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Box
            sx={{
              display: "flex",
              direction: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <GradientButtonOutline
              sx={{
                px: 3,
                display: "flex",
                alignItems: "center",
                height: "40px",
              }}
              onClick={() => {
                setFilter({
                  search: "",
                });
                setPagination((prev) => ({ ...prev, page: 1 }));
                fetchData(true);
              }}
            >
              ล้าง
            </GradientButtonOutline>
            <GradientButton
              sx={{
                px: 3,
                display: "flex",
                alignItems: "center",
                height: "40px",
              }}
              endIcon={<Search />}
              onClick={() => fetchData()}
            >
              ค้นหา
            </GradientButton>
          </Box>
        </Grid>
      </Grid>

      <TableData
        loading={loading}
        data={consignmentCars}
        columns={[
          { key: "expand", label: "" },
          {
            key: "car_pictures",
            label: "รูปภาพ",
          },
          { key: "car_code", label: "รหัสรถ" },
          { key: "license_plate", label: "ทะเบียน" },
          { key: "car_brand", label: "ยี่ห้อ" },
          {
            key: "car_price",
            label: "ราคาทุน",
            // render: (row) => formatCurrency2Digit(row.car_price),
          },
          {
            key: "created_at",
            label: "วันที่ฝากขาย",
            render: (row) => formatDateTime(row.created_at),
          },
          {
            key: "status_name",
            label: "สถานะเอกสาร",
            style: {
              textAlign: "center",
            },
            render: (row) => {
              return (
                <ChipCustomize
                  label={row.status_name}
                  status={row.status_name}
                />
              );
            },
          },
          {
            key: "action",
            label: "",
            style: {
              textAlign: "right",
            },
            render: (row) => (
              <Actions
                menuList={[
                  {
                    label: "ดูรายละเอียด",
                    icon: <Visibility />,
                    onClick: () => {
                      // Handle view details action
                      setSelectedCar(row);
                      setIsDetailModalOpen(true);
                      console.log("View details for", row);
                    },
                  },
                  {
                    label: "แก้ไข",
                    icon: <BorderColor />,
                    onClick: () => {
                      // Handle create document action
                      setIsEditMode(true);
                      setSelectedCar(row);
                      setIsModalOpen(true);
                      console.log("Create document for", row);
                    },
                  },
                  {
                    label: "ฟอร์มเอกสาร",
                    icon: <Description />,
                    onClick: () => {
                      navigate(`/car/consignment_cars/form/${row.car_id}`);
                    },
                  },
                ]}
              />
            ),
          },
        ]}
      />
      <CustomPagination
        totalPage={pagination.totalPages}
        page={pagination.page - 1}
        onChangePage={(event, newPage) => {
          setPagination((prev) => ({ ...prev, page: newPage + 1 }));
        }}
        rowsPerPage={pagination.limit}
        onChangeRowsPerPage={(event) => {
          setPagination((prev) => ({
            ...prev,
            limit: event.target.value,
            page: 1,
          }));
        }}
      />

      <ModalConsignmentCar
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={fetchData}
        isEditMode={isEditMode}
        id={isEditMode && selectedCar ? selectedCar.car_id : null}
      />

      <ModalDetailCar
        open={isDetailModalOpen}
        onClose={handleCloseModal}
        rowID={selectedCar ? selectedCar.car_id : null}
        type="consignment_car"
        formlist={[
          {
            id: 1,
            document: "สัญญารับฝากขาย",
            document_type: "car_consignment_contact",
          },
        ]}
      />
    </Box>
  );
};

export default ConsignmentCars;
